import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { userApiActions } from '@mkp/user/state/actions';
// eslint-disable-next-line no-restricted-imports
import { GoBackWriteService } from '@mkp/shared/util-router';
import { selectSelectedAccountId } from '@mkp/account/state';
import { layoutActions } from '@mkp/layout/actions';
import { BreakpointObserverService } from '@mkp/shared/util-device';
import { UserDisplayType } from '@mkp/user/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  isNavigationEnd,
  selectRouteParams,
  selectRouteQueryParams,
  selectRouteUrl,
} from '@mkp/shared/util-router';
import { selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { combineLatest, noop, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { getGoBackValue } from './helpers/go-back.helpers';
import { areLayoutElementsDifferent, getPageLayoutElements } from './helpers/layout-elements';
import { getPageHeader } from './helpers/layout-headers';
import { getLayoutUserDisplayTypeClass, LayoutInputParams } from '@mkp/layout/models';
import { selectLayoutElements, selectTopHeader } from './layout.selectors';

export const handleLayoutElements = createEffect(
  (store = inject(Store), bos = inject(BreakpointObserverService), router = inject(Router)) =>
    getLayoutParams$(router, store, bos).pipe(
      map(getPageLayoutElements),
      concatLatestFrom(() => [store.select(selectLayoutElements), bos.isMobile$]),
      filter(([curr, prev, isMobile]) => areLayoutElementsDifferent(prev, curr, isMobile)),
      switchMap(([layoutElements]) => of(layoutActions.updateLayoutElements(layoutElements)))
    ),
  { functional: true }
);

export const handlePageHeader = createEffect(
  (store = inject(Store), bos = inject(BreakpointObserverService), router = inject(Router)) =>
    getLayoutParams$(router, store, bos).pipe(
      map(getPageHeader),
      concatLatestFrom(() => store.select(selectTopHeader)),
      filter(([curr, prev]) => curr.name !== prev.name),
      switchMap(([config]) => of(layoutActions.updateHeader(config)))
    ),
  { functional: true }
);

export const handleGoBackValue = createEffect(
  (
    store = inject(Store),
    goBackService = inject(GoBackWriteService),
    isMobile$ = inject(BreakpointObserverService).isMobile$
  ) =>
    store.select(selectRouteUrl).pipe(
      distinctUntilChanged(),
      filter(Boolean),
      startWith(''),
      pairwise(),
      concatLatestFrom(() => [
        store.select(selectRouteParams),
        store.select(selectRouteQueryParams),
        isMobile$,
        store.select(selectSelectedAccountId),
      ]),
      tap(([[prev, curr], params, queryParams, isMobile, accountId]) => {
        // Provide fallback workspaceId from store when navigating from non-workspace routes
        const paramsWithWorkspace = { ...params, workspaceId: params['workspaceId'] ?? accountId };
        const goBackValue = getGoBackValue(prev, curr, paramsWithWorkspace, queryParams, isMobile);
        if (goBackValue) {
          goBackService.setRoute(goBackValue.route);
          goBackService.setTitle(goBackValue.title);
        }
      })
    ),
  { functional: true, dispatch: false }
);

export const updateLayoutUserDisplayType = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(userApiActions.readLoggedInUserSuccess),
      concatLatestFrom(() => store.select(selectLoggedInUser)),
      tap(([{ user }]) => setLayoutUserDisplayType(user.settings.displayType))
    ),
  { functional: true, dispatch: false }
);

const getLayoutParams$ = (
  router: Router,
  store: Store,
  bos: BreakpointObserverService
): Observable<LayoutInputParams> =>
  combineLatest({
    url: router.events.pipe(
      filter(isNavigationEnd),
      map((event) => event.urlAfterRedirects),
      distinctUntilChanged()
    ),
    isMobile: bos.isMobile$.pipe(distinctUntilChanged()),
    isTablet: bos.isTablet$.pipe(distinctUntilChanged()),
    user: store
      .select(selectLoggedInUser)
      .pipe(distinctUntilChanged((previous, current) => previous?.id === current?.id)),
  });

const setLayoutUserDisplayType = (userDisplayType: UserDisplayType): void => {
  const layoutDisplayType = getLayoutUserDisplayTypeClass(userDisplayType);
  layoutDisplayType ? document.body.classList.add(layoutDisplayType) : noop();
};
